import { useEffect, useRef, useState } from 'react';

import { Modal } from '~components/Modals/Base';
import { SubHeadline, Text, Title } from '~components/Typography';
import { routes } from '~constants/routes';
import { useAccountContext } from '~context/account';
import { usePaywallContext } from '~context/paywall';
import {
	PaymentReason,
	PaymentReasonType,
} from '~features/annals/enums/payment';
import annalsBotWizard from '~features/annals/hooks/botWizard';
import { useModal } from '~hooks/useModal';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import { useRouter } from '~i18n/navigation';
import CloseIcon from '~public/assets/icons/close.svg';
import { botsApi } from '~services/api/bots';
import {
	generationApi,
	generationApiErrorCodes,
} from '~services/api/generation';
import type { BotWizardApiGenerateBotPrompt } from '~services/api/generation/types';
import { ButtonPrimary } from '~shared/ui/atoms/Buttons/ButtonPrimary';
import { ButtonSecondary } from '~shared/ui/atoms/Buttons/ButtonSecondary';
import { NeuronImage } from '~shared/ui/atoms/NeuronImage';
import { Popover } from '~shared/ui/atoms/Popover';
import { ToggleSwitch } from '~shared/ui/atoms/ToggleSwitch';
import { CardButton } from '~shared/ui/organisms/CardButton';

import { switchPaycase } from '../../model/utils';
import { ErrorModal } from '../ErrorModal';
import { CloseButton } from '../styles';
import {
	ButtonsWrapper,
	DecisionWrapper,
	PublicButton,
	styledCard,
	StyledResultModalWrapper,
	Variants,
	VariantsWrapper,
} from './styles';

type Props = {
	open: boolean;
	close: () => void;
	bot?: BotWizardApiGenerateBotPrompt;
	isPromptPath: boolean;
};

const SubscriptionAction = ({ name }: { name: string }) => {
	const { openPaywall } = usePaywallContext();
	const t = useTranslations('BotWizard');

	return (
		<ButtonPrimary
			size="L"
			fullWidth={true}
			onClick={openPaywall({
				reason: PaymentReason.NewBotFirstOpen,
				reasonType: PaymentReasonType.GToolBot,
				prefer: 'subscription',
				texts: {
					subscription: t('resultPopupPaywallText', { name }),
				},
			})}
		>
			{t('resultPopupPaywallActionText')}
		</ButtonPrimary>
	);
};

const NeuronsAction = ({
	cost,
	loading,
}: {
	cost: number;
	loading: boolean;
}) => {
	const t = useTranslations('BotWizard');
	const { openPaywall } = usePaywallContext();

	return (
		<ButtonPrimary
			size="M"
			loading={loading}
			disabled={loading}
			rightIcon={<NeuronImage />}
			onClick={openPaywall({
				reason: PaymentReason.NewBotFirstOpen,
				reasonType: PaymentReasonType.GToolBot,
				prefer: 'neurons',
				cost,
			})}
		>
			{t('resultPopupGoChatWithPriceButton', {
				cost,
			})}
		</ButtonPrimary>
	);
};

const FreeAction = ({
	activating,
	onActivate,
}: {
	activating: boolean;
	onActivate: () => void;
}) => {
	const t = useTranslations('BotWizard');
	return (
		<ButtonPrimary
			size="M"
			loading={activating}
			disabled={activating}
			onClick={onActivate}
		>
			{t('resultPopupGoChatWithPriceButton', {
				cost: 'FREE',
			})}
		</ButtonPrimary>
	);
};

const PaidAction = ({
	cost,
	activating,
	onActivate,
}: {
	cost: number;
	activating: boolean;
	onActivate: () => void;
}) => {
	const t = useTranslations('BotWizard');
	return (
		<ButtonPrimary
			size="M"
			loading={activating}
			disabled={activating}
			rightIcon={cost ? <NeuronImage /> : undefined}
			onClick={onActivate}
		>
			{t('resultPopupGoChatWithPriceButton', {
				cost: cost ? cost : 'FREE',
			})}
		</ButtonPrimary>
	);
};

export const ResultModal = ({ open, bot, isPromptPath, close }: Props) => {
	const t = useTranslations('BotWizard');
	const [isPublic, setIsPublic] = useState<boolean>(isPromptPath);
	const [isConfirm, setIsConfirm] = useState<boolean>(false);
	const [avatarIndex, setAvatarIndex] = useState<number>(0);

	const { openPaywall } = usePaywallContext();
	const popoverRef = useRef(null);
	const { isOpen, open: openErrorModal, close: closeErrorModal } = useModal();
	const { balance, hasSubscription } = useAccountContext();
	const { userId, isDemoUser } = useSession();
	const router = useRouter();
	const { data: generationQuota } = generationApi.useGetGenerationBotQuotaQuery(
		{ userId },
		{
			skip: isDemoUser,
			refetchOnMountOrArgChange: true,
		}
	);
	const freeGenerations = generationQuota?.freeGenerations || 0;
	const cost = generationQuota?.price || 0;
	const [paycase, annalPaycase] = switchPaycase(
		hasSubscription,
		freeGenerations > 0,
		balance >= cost
	);
	const sendGenerateBotActivateUsingNeurons =
		annalsBotWizard.useGenerateBotActivateUsingNeurons();

	const sendGenerateBotGenerationStep =
		annalsBotWizard.useGenerateBotGenerationStep();
	const sendGenerateBotActivate = annalsBotWizard.useGenerateBotActivate();
	const [getBotProfile] = botsApi.useLazyGetBotProfileQuery();
	const [activate, { isLoading: isLoadingActivation, reset }] =
		generationApi.useActivateGeneratedBotMutation();

	useEffect(() => {
		if (open && paycase === 'subscription') {
			openPaywall({
				reason: PaymentReason.NewBotFirstOpen,
				reasonType: PaymentReasonType.GToolBot,
				prefer: 'subscription',
			})();
		}
	}, [open, openPaywall, paycase]);

	useEffect(() => {
		if (open) {
			sendGenerateBotGenerationStep({
				status: annalPaycase,
				zone: isDemoUser ? 'non-auth' : 'auth',
			});
		}

		//do it only on open
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		setIsPublic(isPromptPath);
	}, [isPromptPath]);

	const handleActivate = () => {
		if (bot) {
			activate({
				userId,
				isPublic,
				botId: bot.created.id,
				selectedAvatarIndex: avatarIndex,
			})
				.unwrap()
				.then(async (data) => {
					if (paycase === 'paid') {
						sendGenerateBotActivateUsingNeurons();
					}
					sendGenerateBotActivate({ look_number: avatarIndex + 1 });
					await getBotProfile({ userId, recipientId: data.clientId }).unwrap();

					router.push(routes.chats.dialog(data.clientId));
				})
				.catch((_) => {
					openErrorModal()();
				});
		}
	};

	if (!bot) {
		return null;
	}
	return (
		<>
			<Modal open={isConfirm} locked={true} onClose={() => setIsConfirm(false)}>
				<StyledResultModalWrapper className="exit">
					<SubHeadline size="L">
						{t('resultPopupExitWarning', {
							name: bot.created.info.name,
						})}
					</SubHeadline>
					<DecisionWrapper>
						<ButtonSecondary
							size="M"
							fullWidth={true}
							onClick={() => {
								setIsConfirm(false);
								close();
							}}
						>
							{t('resultPopupExitWarningNo')}
						</ButtonSecondary>
						<ButtonPrimary
							onClick={() => setIsConfirm(false)}
							size="M"
							fullWidth={true}
						>
							{t('resultPopupExitWarningYes')}
						</ButtonPrimary>
					</DecisionWrapper>
				</StyledResultModalWrapper>
			</Modal>
			<Modal
				locked={true}
				open={open && !isOpen && !isConfirm}
				onClose={() => setIsConfirm(true)}
			>
				<StyledResultModalWrapper>
					<CloseButton
						onClick={() => setIsConfirm(true)}
						leftIcon={<CloseIcon />}
					/>
					<Title size="M">
						{t('resultPopupTitle', {
							name: bot?.created.info.name,
						})}
					</Title>
					<Variants>
						<VariantsWrapper>
							{bot.created.info.avatarVariants.length > 0 &&
								bot.created.info.avatarVariants.map((variant, index) => (
									<CardButton
										selected={avatarIndex === index}
										onClick={() => setAvatarIndex(index)}
										className={styledCard}
										key={index}
										item={variant.gridLinks[0]}
										image=""
										aspectRatio="vertical"
										width={1000}
										height={1000}
									/>
								))}
						</VariantsWrapper>
					</Variants>
					<ButtonsWrapper>
						{paycase === 'subscription' && (
							<SubscriptionAction name={bot?.created.info.name} />
						)}
						{paycase === 'neurons' && (
							<NeuronsAction loading={isLoadingActivation} cost={cost} />
						)}
						{paycase === 'free' && (
							<FreeAction
								activating={isLoadingActivation}
								onActivate={() => handleActivate()}
							/>
						)}
						{paycase === 'paid' && (
							<PaidAction
								cost={cost}
								activating={isLoadingActivation}
								onActivate={() => handleActivate()}
							/>
						)}
						<PublicButton
							ref={popoverRef}
							disabled={isLoadingActivation}
							leftIcon={
								<ToggleSwitch
									height={20}
									width={36}
									activeColor="var(--color-rose-58-solid)"
									thumbSize={16}
									checked={isPublic}
								/>
							}
							onClick={() => setIsPublic((prev) => !prev)}
							disabledIconStyles={true}
							size="M"
						>
							{t('resultPopupBotAvailable')}
						</PublicButton>
						<Popover placement="bottom" ref={popoverRef}>
							<Text size="M" align="left">
								{t('resultPopupTooltip')}
							</Text>
						</Popover>
					</ButtonsWrapper>
				</StyledResultModalWrapper>
			</Modal>
			<ErrorModal
				open={isOpen}
				errorCode={generationApiErrorCodes.unknown}
				onClose={closeErrorModal}
				onAction={() => {
					reset();
					handleActivate();
					close();
					setIsConfirm(false);
				}}
			/>
		</>
	);
};
